// ============================================================
// 1. UTILITIES (Helper functions for DOM and Selectize manipulation)
// ============================================================
const UI = {
  // Shortcut for document.getElementById
  get: (id) => document.getElementById(id),
  
  // Show or hide an element (and handle specific opacity for step-2)
  toggle: (el, show) => {
    if (!el) return;
    show ? el.classList.remove('d-none') : el.classList.add('d-none');
    if (show && el.id === 'step-2') el.style.opacity = '1'; 
  },
  
  // Resets an HTML <select> and its Selectize instance if it exists
  resetSelect: (el, placeholder = '') => {
    if (!el) return;
    el.innerHTML = `<option value="" disabled selected>${placeholder}</option>`;
    el.disabled = true;
    
    // Selectize reset (clear cache and disable)
    if (el.selectize) {
      el.selectize.clear();
      el.selectize.clearOptions();
      el.selectize.disable();
      if (placeholder) {
          el.selectize.settings.placeholder = placeholder;
          el.selectize.updatePlaceholder();
      }
    }
  },
  
  // Re-enables a select (native + Selectize)
  enableSelect: (el) => {
    if (!el) return;
    el.disabled = false;
    if (el.selectize) el.selectize.enable();
  },

  // Sets a value (useful for editing)
  setValue: (el, value) => {
      if (!el) return;
      el.value = String(value);
      if (el.selectize) {
          el.selectize.setValue(String(value), false); 
      }
  },

  // Synchronizes native HTML options to the Selectize instance
  // Selectize does not automatically detect .innerHTML changes
  syncSelectize: (el) => {
      if (el && el.selectize) {
          const selectize = el.selectize;
          selectize.clearOptions();
          
          // Repopulate Selectize with DOM options
          Array.from(el.options).forEach(opt => {
              if (opt.value) {
                  selectize.addOption({
                      value: opt.value,
                      text: opt.text
                  });
              }
          });
          selectize.refreshOptions(false);
      }
  },

  // Wrapper for fetch that handles HTTP errors and returns text/HTML
  fetchHtml: async (url, params = {}) => {
      const query = new URLSearchParams(params).toString();
      const target = query ? `${url}?${query}` : url;
      try {
          const res = await fetch(target, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });
          if (!res.ok) {
              const errorMsg = await res.text();
              throw new Error(errorMsg || `HTTP Error ${res.status}`);
          }
          return await res.text();
      } catch (err) {
          throw err;
      }
    }
};

/* ============================================================
 * STEP 1 — NAME VALIDATION (Rule name verification)
 * ============================================================ */
(function () {
  const isEdit       = !!window.__EDIT_MODE__;
  const inputName    = UI.get('rulename');
  const feedback     = UI.get('rulename-feedback');
  const spinner      = UI.get('rulename-spinner');
  const step2Section = UI.get('step-2');

  if (!inputName || !feedback) return;

  let debounceTimer = null;
  let lastValueSent = '';
  let step2Shown    = false;

  const toggleSpinner = (show) => UI.toggle(spinner, show);

  // Displays validation status (green/red) under the input
  function setStatus(status, msg) {
    toggleSpinner(false);
    inputName.classList.remove('is-invalid', 'is-valid');
    feedback.className = 'form-text';
    
    if (status === 'error') {
      inputName.classList.add('is-invalid');
      feedback.classList.add('text-danger');
    } else if (status === 'success') {
      inputName.classList.add('is-valid');
      feedback.classList.add('text-success');
      revealStep2();
    }
    feedback.textContent = msg || '';
  }

  // Reveals the rest of the form (Step 2) with an animation
  function revealStep2() {
    if (step2Shown || !step2Section) return;
    step2Shown = true;
    UI.toggle(step2Section, true);
    step2Section.style.opacity = 0;
    step2Section.style.transition = 'opacity .25s ease';
    requestAnimationFrame(() => { step2Section.style.opacity = 1; });
    if (window.updateRuleNavLinks) window.updateRuleNavLinks();
  }

  // Calls the server to check if the name is unique
  async function checkUniqueness(nameVal) {
    const url = inputName.getAttribute('data-check-url');
    if (!url) return setStatus('error', 'Validation URL missing.');

    try {
      const res = await fetch(url, {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded', 'X-Requested-With': 'XMLHttpRequest' },
        body: new URLSearchParams({ name: nameVal })
      });
      const text = await res.text();
      let existsFlag;
      try { existsFlag = JSON.parse(text); } catch { existsFlag = text; }

      if (existsFlag === 0 || existsFlag === '0') {
        setStatus('success', window.transRuleNameAvailable || 'Name is available.');
      } else {
        setStatus('error', window.transRuleNameTaken || 'This name is already taken.');
      }
    } catch {
      setStatus('error', window.transRuleNameNetworkErr || 'Network error.');
    }
  }

  if (isEdit) return;

  // Validation on Enter key press
  inputName.addEventListener('keydown', (e) => {
    if (e.key === 'Enter') {
      e.preventDefault();
      const v = inputName.value.trim();
      if (v.length < 3) return setStatus('error', window.transRuleNameTooShort || 'Min 3 chars.');
      toggleSpinner(true);
      lastValueSent = v;
      checkUniqueness(v);
    }
  });

  // Validation on typing (with Debounce to avoid spamming the server)
  inputName.addEventListener('input', () => {
    clearTimeout(debounceTimer);
    setStatus('neutral');
    const v = inputName.value.trim();
    if (!v) { toggleSpinner(false); return; }
    toggleSpinner(true);

    if (v.length < 3) {
      debounceTimer = setTimeout(() => {
        setStatus('error', window.transRuleNameTooShort || 'Min 3 chars.');
      }, 300);
      return;
    }

    if (v === lastValueSent && inputName.classList.contains('is-valid')) {
      toggleSpinner(false);
      revealStep2();
      return;
    }

    debounceTimer = setTimeout(() => {
      lastValueSent = v;
      checkUniqueness(v);
    }, 400);
  });
})();

/* ============================================================
 * STEP 2 + 3 + 4 + 5 (CORE LOGIC & LOADERS)
 * Managing cascading dropdowns (Solution -> Connector -> Module)
 * ============================================================ */
(function () {
  const step2 = UI.get('step-2');
  if (!step2) return;

  const PATHS = {
    connectors: step2.getAttribute('data-path-connectors'),
    modules: step2.getAttribute('data-path-module')
  };

 // References to DOM elements for Source (src) and Target (tgt)
 const EL = {
    src: { sol: UI.get('source-solution'), conn: UI.get('source-connector'), mod: UI.get('source-module'), spin: UI.get('source-connector-spinner'), modSpin: UI.get('source-module-spinner'), feed: UI.get('source-connector-feedback') },
    tgt: { sol: UI.get('target-solution'), conn: UI.get('target-connector'), mod: UI.get('target-module'), spin: UI.get('target-connector-spinner'), modSpin: UI.get('target-module-spinner'), feed: UI.get('target-connector-feedback') },
    step3: UI.get('step-3'),
    step4: UI.get('step-4'),
    step5: UI.get('step-5'),
    step4Body: UI.get('step-4-body'),
    paramsContainer: UI.get('step-3-params-container')
  };

  const step3ParamsPath = EL.step3 ? EL.step3.getAttribute('data-path-params') : null;
  let filtersLoaded = false;
  let bootingSteps = false;

  function getVal(el) {
    if (!el) return '';
    if (el.selectize) return (el.selectize.getValue() || '').trim();
    return (el.value || '').trim();
  }

  async function loadSelectData(type, url, params, targetSelect, spinner, feedbackEl) {
    UI.resetSelect(targetSelect, 'Loading...');
    if (feedbackEl) {
      feedbackEl.textContent = '';
      feedbackEl.classList.remove('text-danger', 'text-success');
    }
    if (!url) return;

    try {
      UI.toggle(spinner, true);
      const htmlParams = new URLSearchParams(params).toString();
      const response = await fetch(`${url}?${htmlParams}`, { headers: { 'X-Requested-With': 'XMLHttpRequest' } });

      if (!response.ok) {
        const errorMsg = await response.text();
        throw new Error(errorMsg || `Error ${response.status}`);
      }
      const html = await response.text();

      // Update native HTML
      targetSelect.innerHTML = '';

      if (html) {
        const temp = document.createElement('div');
        temp.innerHTML = html;
        Array.from(temp.querySelectorAll('option')).forEach(opt => targetSelect.appendChild(opt));
      }

      if (targetSelect.selectize) {
        targetSelect.selectize.settings.placeholder = 'Search...';
        targetSelect.selectize.updatePlaceholder();
      }

      // synchronization with Selectize
      UI.syncSelectize(targetSelect);
      UI.enableSelect(targetSelect);

    } catch (e) {
      console.warn("Loading error:", e.message);
      UI.resetSelect(targetSelect, 'Error loading list');
      UI.enableSelect(targetSelect);

      if (feedbackEl) {
        feedbackEl.innerHTML = '<i class="fas fa-exclamation-circle"></i> ' + e.message;
        feedbackEl.className = 'form-text text-danger fw-bold';
      }
    } finally {
      UI.toggle(spinner, false);
    }
  }

  // Loads connectors for a given solution
  const loadConnectorsFor = (side, solutionId) => {
    if (!solutionId) return Promise.resolve();
    const group = side === 'source' ? EL.src : EL.tgt;
    return loadSelectData('connectors', PATHS.connectors, { solution_id: solutionId }, group.conn, group.spin, group.feed);
  };

  // Loads modules for a given connector
  const loadModulesFor = (side, connectorId) => {
    if (!connectorId) return Promise.resolve();
    const group = side === 'source' ? EL.src : EL.tgt;
    const type = side === 'source' ? 'source' : 'cible';
    return loadSelectData('modules', PATHS.modules, { id: connectorId, type: type }, group.mod, group.modSpin, group.feed);
  };

// Loads specific Step 3 parameters (date fields, limit, etc.)
  async function loadStep3Params() {
    if (!EL.step3 || !step3ParamsPath || !EL.paramsContainer) return;

    const valSrcConn = getVal(EL.src.conn);
    const valTgtConn = getVal(EL.tgt.conn);
    const valSrcMod = getVal(EL.src.mod);
    const valTgtMod = getVal(EL.tgt.mod);

    if (!valSrcConn || !valTgtConn || !valSrcMod || !valTgtMod) {
      EL.paramsContainer.innerHTML = '';
      return;
    }

    const params = {
      src_connector: valSrcConn,
      tgt_connector: valTgtConn,
      src_module: valSrcMod,
      tgt_module: valTgtMod
    };

    try {
      EL.paramsContainer.innerHTML = '<div class="text-center py-3"><div class="spinner-border text-primary" role="status"></div></div>';

      const html = await UI.fetchHtml(step3ParamsPath, params);
      EL.paramsContainer.innerHTML = html;

      $('.js-select-search', EL.paramsContainer).selectize({
        sortField: 'text',
        placeholder: 'Search...',
      });

      const dupSelect = UI.get('duplicate-field');
      const badgesContainer = UI.get('duplicate-badges-container');

      if (dupSelect && badgesContainer) {

        const addBadge = (val, label) => {
          if (badgesContainer.querySelector(`[data-field="${CSS.escape(val)}"]`)) return;

          const badge = document.createElement('span');
          badge.className = 'mapping-src-badge rounded-pill px-2 me-2 mb-2 d-inline-flex align-items-center';
          badge.dataset.field = val;
          badge.innerHTML = `<span class="mapping-src-badge-label">${label}</span><button type="button" class="p-0 ms-2 mapping-src-badge-remove">&times;</button>`;

          badge.querySelector('button').onclick = () => {
            badge.remove();
            const tbody = UI.get('rule-mapping-body');
            if (tbody) {
              tbody.querySelectorAll('button.text-danger').forEach(b => {
                b.disabled = false; b.style.opacity = '1'; b.style.pointerEvents = 'auto';
                b.onclick = function () {
                  this.closest('tr').remove();
                };
              });
              Array.from(badgesContainer.querySelectorAll('.mapping-src-badge')).forEach(b => {
                if (window.ensureDuplicateMappingRow) window.ensureDuplicateMappingRow(b.dataset.field);
              });
            }
          };

          badgesContainer.appendChild(badge);
          if (window.ensureDuplicateMappingRow) window.ensureDuplicateMappingRow(val);
        };
        $(dupSelect).on('change', function () {
          const val = $(this).val();
          if (!val) return;

          let text = val;
          if (this.selectize) {
            const item = this.selectize.getItem(val);
            if (item.length) text = item.text();
            this.selectize.clear(true);
          } else {
            text = this.options[this.selectedIndex].text;
            this.value = '';
          }

          addBadge(val, text);
          if (step3IsComplete()) revealStep4and5();
        });
        if (window.initialRule && window.initialRule.syncOptions) {
          const opts = window.initialRule.syncOptions;
          const raw = opts.duplicateField || opts.duplicate_fields || opts.duplicateFields;
          if (raw) {
            let vals = [];
            if (Array.isArray(raw)) vals = raw;
            else if (typeof raw === 'string') vals = raw.split(';');

            vals.forEach(v => {
              if (v && v.trim() !== '') {
                addBadge(v.trim(), v.trim());
              }
            });
            if (dupSelect && vals.length > 0) {
              if (dupSelect.selectize) dupSelect.selectize.clear(true);
              else dupSelect.value = '';
            }
          }
        }
      }
      const modeSelect = UI.get('mode');
      if (modeSelect) {
        modeSelect.addEventListener('change', () => { if (step3IsComplete()) revealStep4and5(); });
        if (modeSelect.value) revealStep4and5();
      }

    } catch (e) {
      console.error(e);
      EL.paramsContainer.innerHTML = '<div class="alert alert-danger">Unable to load parameters. (' + e.message + ')</div>';
    }
  }

  function step3IsComplete() {
    const modeEl = UI.get('mode');
    return !!(modeEl && modeEl.value);
  }

  function bothModulesSelected() {
    return !!(getVal(EL.src.mod) && getVal(EL.tgt.mod));
  }

  // Progressive display of steps
  function revealStep3() {
    if (!EL.step3) return;
    UI.toggle(EL.step3, true);
    if (window.updateRuleNavLinks) window.updateRuleNavLinks();
  }

  async function tryRevealStep3() {
    if (!EL.step3) return;
    if (!bothModulesSelected()) return;

    bootingSteps = true;
    UI.toggle(EL.step3, false);
    UI.toggle(EL.step4, false);
    UI.toggle(EL.step5, false);

    const isEdit = !!window.__EDIT_MODE__;
    let loader = null;
    if (!isEdit) {
      loader = document.getElementById('steps-345-loader');
      if (!loader) {
        loader = document.createElement('div');
        loader.id = 'steps-345-loader';
        loader.style.cssText = 'position:fixed;inset:0;display:none;align-items:center;justify-content:center;background:rgba(255,255,255,.6);z-index:9999;';
        loader.innerHTML = '<div class="spinner-border" role="status"></div>';
        document.body.appendChild(loader);
      }
      loader.style.display = 'flex';
    }

    try {
      if (window.mydLoadRuleFilters && !filtersLoaded) {
        await window.mydLoadRuleFilters();
        filtersLoaded = true;
      }

      await loadStep3Params();

      UI.toggle(EL.step3, true);
      UI.toggle(EL.step4, true);
      UI.toggle(EL.step5, true);
      updateMappingHeaders();

      if (window.updateRuleNavLinks) window.updateRuleNavLinks();
    } finally {
      if (loader) loader.style.display = 'none';
      bootingSteps = false;
    }
  }

  
  // Reset lower steps if a parent module changes
  function resetStep3AndBelow() {
    if (EL.paramsContainer) EL.paramsContainer.innerHTML = '';
    if (EL.step4Body) EL.step4Body.innerHTML = '';
    filtersLoaded = false;
    const mapBody = UI.get('rule-mapping-body');
    if (mapBody) {
      mapBody.innerHTML = '';
      mapBody.removeAttribute('data-loaded-module');
    }
    if (window.resetTargetFieldsCache) window.resetTargetFieldsCache();

    UI.toggle(EL.step3, false);
    UI.toggle(EL.step4, false);
    UI.toggle(EL.step5, false);
  }

  // Loads the Filters UI (Step 4)
  const loadFiltersUI = async () => {
    if (!EL.step4 || !EL.step4Body) return;
    const pathFilter = EL.step4.getAttribute('data-path-filters');
    if (!pathFilter) return;

    const params = {
      src_solution_id: EL.src.sol.value,
      tgt_solution_id: EL.tgt.sol.value,
      src_module: EL.src.mod.value,
      tgt_module: EL.tgt.mod.value,
      src_connector_id: EL.src.conn.value,
      tgt_connector_id: EL.tgt.conn.value
    };

    const rid = new URLSearchParams(location.search).get('rule_id');
    if (rid) params.rule_id = rid;

    try {
      const html = await UI.fetchHtml(pathFilter, params);
      EL.step4Body.innerHTML = html;
      if (window.buildFilterFieldOptions) window.buildFilterFieldOptions();
      if (window.initFiltersUI) window.initFiltersUI();
      if (window.initMappingUI) window.initMappingUI();
      if (window.autoMapRequiredFields) await window.autoMapRequiredFields();
    } catch (e) {
      EL.step4Body.innerHTML = '<p class="text-danger">Unable to load filters. (' + e.message + ')</p>';
    }
    console.groupEnd();
  };
  window.mydLoadRuleFilters = loadFiltersUI;

  function updateMappingHeaders() {
    const thTarget = document.getElementById('mapping-th-target');
    const thSource = document.getElementById('mapping-th-source');
    if (!thTarget || !thSource) return;

    const rootPath = window.location.pathname.split('/rule')[0];

    const buildHeader = (selectEl) => {
      if (!selectEl || !selectEl.value) return null;
      const opt = selectEl.options[selectEl.selectedIndex];
      if (!opt) return null;
      const name = opt.text.trim();
      const slug = (opt.getAttribute('data-solution-slug') || name).toLowerCase();
      const imgUrl = rootPath + '/rule/assets/solution-icon/' + encodeURIComponent(slug);
      return '<span class="d-flex align-items-center gap-2">' +
             '<img src="' + imgUrl + '" style="width:20px;height:20px;object-fit:contain" onerror="this.style.display=\'none\'">' +
             '<span>' + name + '</span></span>';
    };

    const tgtHtml = buildHeader(EL.tgt.sol);
    const srcHtml = buildHeader(EL.src.sol);
    if (tgtHtml) thTarget.innerHTML = tgtHtml;
    if (srcHtml) thSource.innerHTML = srcHtml;
  }

  function revealStep4and5() {
    if (bootingSteps) return;
    if (EL.step4 && EL.step4.classList.contains('d-none')) {
      UI.toggle(EL.step4, true);
      if (window.mydLoadRuleFilters && !filtersLoaded) {
        window.mydLoadRuleFilters();
        filtersLoaded = true;
      }
    }
 if (EL.step5) {
      UI.toggle(EL.step5, true);
      updateMappingHeaders();
      // initMappingUI is called from loadFiltersUI after fields are loaded
    }
    if (window.updateRuleNavLinks) window.updateRuleNavLinks();
  }

  // --- LISTENERS ---
  EL.src.sol?.addEventListener('change', () => {
    UI.resetSelect(EL.src.conn); UI.resetSelect(EL.src.mod);
    loadConnectorsFor('source', EL.src.sol.value);
  });
  EL.tgt.sol?.addEventListener('change', () => {
    UI.resetSelect(EL.tgt.conn); UI.resetSelect(EL.tgt.mod);
    loadConnectorsFor('cible', EL.tgt.sol.value);
  });
  EL.src.conn?.addEventListener('change', () => loadModulesFor('source', EL.src.conn.value));
  EL.tgt.conn?.addEventListener('change', () => {
    loadModulesFor('cible', EL.tgt.conn.value);
    if (EL.tgt.mod.value) tryRevealStep3();
  });
  EL.src.mod?.addEventListener('change', () => { resetStep3AndBelow(); tryRevealStep3(); });
  EL.tgt.mod?.addEventListener('change', () => { resetStep3AndBelow(); tryRevealStep3(); });

  // Expose globally for Edit mode
  window.loadConnectorsFor = loadConnectorsFor;
  window.loadModulesFor = loadModulesFor;
  window.tryRevealStep3 = tryRevealStep3;
  window.loadStep3Params = loadStep3Params;
})();

/* ============================================================
 * FILTERS & MAPPING UI FUNCTIONS (Global)
 * ============================================================ */
(function() {
// Retrieves JSON attributes (data-fields) from options
  const getJsonAttr = (el, attr) => {
    if (!el || !el.value) return null;
    const opt = el.options[el.selectedIndex];
    try { return JSON.parse(opt.getAttribute(attr)); } catch { return null; }
  };

  // Builds the filter field select options
  window.buildFilterFieldOptions = function() {
    const filterSelect = UI.get("rule-filter-field");
    if (!filterSelect) return;

    if (filterSelect.selectize) {
      UI.syncSelectize(filterSelect);
    } else {
      $(filterSelect).selectize({
        sortField: "text",
        lockOptgroupOrder: true,
        placeholder: "Choose a field...",
        dropdownParent: "body"
      });
    }
  };

window.autoMapRequiredFields = async function() {
  const tgtConn = document.getElementById('target-connector')?.value;
  const tgtMod  = document.getElementById('target-module')?.value;
  const srcConn = document.getElementById('source-connector')?.value;
  const srcMod  = document.getElementById('source-module')?.value;
  const tbody   = document.getElementById('rule-mapping-body');
  const step5   = document.getElementById('step-5');

  if (!tgtConn || !tgtMod || !tbody || !step5) return;
  if (tbody.getAttribute('data-loaded-module') === tgtMod) return;

  const urlBase = step5.getAttribute('data-path-mapping-initial');
  if (!urlBase) return;

  const getVal = (sel) => {
    if (!sel) return '';
    if (sel.selectize) return (sel.selectize.getValue() || '').trim();
    if (window.jQuery && window.jQuery(sel)[0] && window.jQuery(sel)[0].selectize) {
      return (window.jQuery(sel)[0].selectize.getValue() || '').trim();
    }
    return (sel.value || '').trim();
  };

  try {
    const params = new URLSearchParams({
      connector_id: tgtConn,
      module: tgtMod,
      src_connector_id: srcConn,
      src_module: srcMod
    });

    const res = await fetch(`${urlBase}?${params.toString()}`, {
      headers: { 'X-Requested-With': 'XMLHttpRequest' }
    });

    if (!res.ok) return;
    const html = await res.text();
    if (!html || html.trim().length === 0) return;

    const rowsBefore = Array.from(tbody.querySelectorAll('tr'));
    const emptyRow = rowsBefore.find(tr => {
      const tgtSel = tr.querySelector('.rule-mapping-target');
      const tgtVal = getVal(tgtSel);
      const badges = tr.querySelectorAll('.mapping-src-badge');
      const formulaInput = tr.querySelector('.rule-mapping-formula-input');
      const formulaVal = formulaInput ? (formulaInput.value || '').trim() : '';
      return !tgtVal && badges.length === 0 && !formulaVal;
    });

    const temp = document.createElement('tbody');
    temp.innerHTML = html;
    const newRows = Array.from(temp.querySelectorAll('tr'));
    if (!newRows.length) return;

    if (emptyRow) {
      emptyRow.replaceWith(newRows[0]);
      let anchor = newRows[0];
      for (let i = 1; i < newRows.length; i++) {
        anchor.insertAdjacentElement('afterend', newRows[i]);
        anchor = newRows[i];
      }
    } else {
      newRows.forEach(r => tbody.appendChild(r));
    }

    initNewRows(tbody);
    tbody.setAttribute('data-loaded-module', tgtMod);
    window.refreshTargetDropdowns();

  } catch (e) {
    console.error('Auto-mapping error:', e);
  }
};

function initNewRows(container) {
    $(container).find('.js-select-search-locked').each(function() {
        if (!this.selectize) {
            $(this).selectize({ sortField: 'text' });
        }
    });

    $(container).find('.js-select-search-source').each(function() {
        if (this.selectize) return;

        const wrapper = this.closest('.mapping-src-wrapper');
        const badgesDiv = wrapper ? wrapper.querySelector('.mapping-src-badges') : null;

        $(this).selectize({
            sortField: 'text',
            placeholder: 'Search Source...',
            dropdownParent: 'body',
            onChange: function(value) {
                if (!value || !badgesDiv) return;
                
                if(badgesDiv.querySelector(`[data-field="${CSS.escape(value)}"]`)) {
                    this.clear(true);
                    return;
                }
                let txt = value;
                const item = this.getItem(value);
                if(item.length) txt = item.text();
                const badge = document.createElement('span');
                badge.className = 'mapping-src-badge rounded-pill px-2 me-2 mb-2 d-inline-flex align-items-center';
                badge.dataset.field = value;
                badge.innerHTML = `<span class="mapping-src-badge-label">${txt}</span><button type="button" class="p-0 ms-2 mapping-src-badge-remove">&times;</button>`;
                badge.querySelector('button').onclick = () => badge.remove();
                badgesDiv.appendChild(badge);

                this.clear(true); 
            }
        });
    });

    $(container).find('.rule-mapping-formula').off('click').on('click', function() {
        const btn = this;
        const tr = btn.closest('tr');
        const hidden = tr.querySelector('.rule-mapping-formula-input');
        const container = UI.get('formula-selected-fields');
        const modal = UI.get('mapping-formula');
        const area = UI.get('area_insert');

        if (container && modal) {
            container.innerHTML = '';
            const badges = tr.querySelectorAll('.mapping-src-badge');
            
            if (badges.length === 0) {
                container.innerHTML = '<span class="text-muted">No field selected in source</span>';
            } else {
                badges.forEach(b => {
                    const chip = document.createElement('span');
                    chip.className = 'badge-formula rounded-pill px-3 mb-3';
                    chip.textContent = b.querySelector('.mapping-src-badge-label').textContent;
                    chip.dataset.field = b.dataset.field;
                    container.appendChild(chip);
                });
            }
            
            modal.dataset.currentRowId = tr.dataset.rowId;
            modal.dataset.currentFormulaInputId = hidden.id;
            if (area) area.value = hidden.value || '';
        }
    });
    $(container).find('.rule-mapping-delete').off('click').on('click', function() {
        $(this).closest('tr').remove();
    });
  }

  window.addFilterRow = function(fieldVal, opVal, valueVal) {
      const listWrap = UI.get('rule-filters-list');
      const fieldSelect = UI.get('rule-filter-field');
      const opSelect = UI.get('rule-filter-operator');
      
      if (!listWrap || !fieldVal || !opVal) return;
      let fieldLabel = fieldVal;
      let opLabel = opVal;

      if (fieldSelect) {
          const opt = Array.from(fieldSelect.querySelectorAll('option')).find(o => o.value === fieldVal);
          if (opt) fieldLabel = opt.textContent;
      }
      if (opSelect) {
          const opt = Array.from(opSelect.options).find(o => o.value === opVal);
          if (opt) opLabel = opt.textContent;
      }

      const emptyMsg = listWrap.querySelector('p.text-muted');
      if (emptyMsg) emptyMsg.remove();

      let ul = listWrap.querySelector('ul');
      if (!ul) {
        ul = document.createElement('ul');
        ul.className = 'list-group';
        listWrap.appendChild(ul);
      }

      const li = document.createElement('li');
      li.className = 'list-group-item d-flex justify-content-between align-items-center';
      li.dataset.field = fieldVal;
      li.dataset.operator = opVal;
      li.dataset.value = valueVal;
      li.innerHTML = `<span><strong>${fieldLabel}</strong> <small class="text-muted">(${opLabel})</small> = ${valueVal}</span>`;

      const btnGroup = document.createElement('div');
      btnGroup.className = 'd-flex gap-1';

      const editBtn = document.createElement('button');
      editBtn.className = 'btn btn-sm text-primary myddleware-blue-pen';
      editBtn.innerHTML = '<i class="fa-solid fa-pen"></i>';
      editBtn.type = 'button';
      editBtn.title = 'Edit filter';
      editBtn.style.setProperty('color', '#05bfe6', 'important');
      editBtn.onclick = () => {
        const valInput = UI.get('rule-filter-value');

        if (fieldSelect) {
          fieldSelect.value = fieldVal;
          if (fieldSelect.selectize) fieldSelect.selectize.setValue(fieldVal, false);
        }
        if (opSelect) {
          opSelect.value = opVal;
          if (opSelect.selectize) opSelect.selectize.setValue(opVal, false);
        }
        if (valInput) {
          valInput.value = valueVal;
          valInput.focus();
        }

        li.remove();
        if (!ul.children.length) listWrap.innerHTML = '<p class="text-muted mb-0">No filters have been defined yet.</p>';
      };

      const delBtn = document.createElement('button');
      delBtn.className = 'btn btn-sm text-danger';
      delBtn.innerHTML = '<i class="fa-solid fa-trash"></i>';
      delBtn.type = 'button';
      delBtn.title = 'Delete filter';
      delBtn.onclick = () => {
        li.remove();
        if (!ul.children.length) listWrap.innerHTML = '<p class="text-muted mb-0">No filters have been defined yet.</p>';
      };

      btnGroup.appendChild(editBtn);
      btnGroup.appendChild(delBtn);
      li.appendChild(btnGroup);
      ul.appendChild(li);
  };

  // Initializes the add filter button
  window.initFiltersUI = function() {
      const addBtn = UI.get('rule-filter-add');
      if (!addBtn || addBtn.dataset.bound) return;
      addBtn.dataset.bound = '1';      
      addBtn.addEventListener('click', () => {
        const fieldSel = UI.get('rule-filter-field');
        const opSel = UI.get('rule-filter-operator');
        const valInput = UI.get('rule-filter-value');        
        let fieldVal = fieldSel.value;
        if (fieldSel.selectize) fieldVal = fieldSel.selectize.getValue();
        if (!fieldVal || !opSel.value) return; 
        window.addFilterRow(fieldVal, opSel.value, valInput.value.trim());
        
        if (fieldSel.selectize) {
            fieldSel.selectize.clear();
        } else {
            fieldSel.value = ''; 
        }
        opSel.value = ''; 
        valInput.value = '';
      });
  };

  function createMappingSelect(fields) {
    const sel = document.createElement('select');
    sel.appendChild(new Option('', '', true, true));
    if (fields) Object.entries(fields).forEach(([v, t]) => sel.appendChild(new Option(v, v)));
    return sel;
  }

  function genRowId() {
    return 'row-' + Date.now() + '-' + Math.random().toString(36).substr(2, 9);
  }

// Adds a row to the Mapping table (Step 5)
window.addMappingRow = function(tbody, preselectedTarget = null, isRequired = false) {
    // Read fields from embedded JSON (from filter template)
    let srcFields = {}, tgtFields = {};
    const fieldsDataEl = document.getElementById('rule-fields-data');
    if (fieldsDataEl) {
      try {
        const data = JSON.parse(fieldsDataEl.textContent);
        srcFields = data.source || {};
        tgtFields = data.target || {};
      } catch (e) { console.warn('Error parsing fields data:', e); }
    }

    const tr = document.createElement('tr');
    tr.dataset.rowId = genRowId();

    // Target Select
    const tdTgt = document.createElement('td');
    const tgtSel = createMappingSelect(tgtFields);
    tgtSel.classList.add('rule-mapping-target', 'js-select-search');

    if (preselectedTarget) {
        tgtSel.value = preselectedTarget;
    }
    
    tdTgt.appendChild(tgtSel);

    // Source Select + Badges (Multiple Source Fields)
    const tdSrc = document.createElement('td');
    const srcWrapper = document.createElement('div');
    srcWrapper.className = 'mapping-src-wrapper';
    const srcSel = createMappingSelect(srcFields);
    srcSel.classList.add('rule-mapping-source-picker', 'js-select-search'); 
    const badgesDiv = document.createElement('div');
    badgesDiv.className = 'mapping-src-badges pt-1';

    // Manage multiple addition (Badges)
    srcSel.addEventListener('change', () => {
      const val = srcSel.value;    
      if (!val || badgesDiv.querySelector(`[data-field="${CSS.escape(val)}"]`)) {
        if(srcSel.selectize) srcSel.selectize.clear(true);
        else srcSel.value = '';
        return;
      }
      let txt = val;
      if (srcSel.selectize) {
          const item = srcSel.selectize.getItem(val);
          if (item.length) txt = item.text();
      } else if (srcSel.options[srcSel.selectedIndex]) {
          txt = srcSel.options[srcSel.selectedIndex].text;
      }
      const badge = document.createElement('span');
      badge.className = 'mapping-src-badge rounded-pill px-2 me-2 mb-2 d-inline-flex align-items-center';
      badge.dataset.field = val;
      badge.innerHTML = `<span class="mapping-src-badge-label">${txt}</span><button type="button" class="p-0 ms-2 mapping-src-badge-remove">&times;</button>`;     
      badge.querySelector('button').onclick = () => badge.remove();
      badgesDiv.appendChild(badge);
    
      if (srcSel.selectize) srcSel.selectize.clear(true); else srcSel.value = '';
    });

    srcWrapper.append(srcSel, badgesDiv);
    tdSrc.appendChild(srcWrapper);

   // Actions (Formula Button)
    const tdAct = document.createElement('td');
    tdAct.className = 'd-flex align-items-center';
    const slot = document.createElement('div');
    slot.className = 'formula-slot is-empty';
    slot.textContent = '...';   
    const btn = document.createElement('button');
    btn.className = 'btn btn-sm ms-2 rule-mapping-formula';
    btn.innerHTML = '<i class="fa fa-code"></i>';
    btn.type = 'button';
    btn.setAttribute('data-bs-toggle', 'modal');
    btn.setAttribute('data-bs-target', '#mapping-formula');
    
    const hidden = document.createElement('input');
    hidden.type = 'hidden'; 
    hidden.className = 'rule-mapping-formula-input'; 
    hidden.name = 'mapping_formula[]';
    hidden.id = `formula-input-${tr.dataset.rowId}`; 

    tdAct.append(slot, btn, hidden);

    // Open formula modal with row context
    btn.onclick = () => {
      const container = UI.get('formula-selected-fields');
      const modal = UI.get('mapping-formula');
      if (container && modal) {
        container.innerHTML = '';
        const badges = tr.querySelectorAll('.mapping-src-badge');      
        if (!badges.length) container.innerHTML = '<span class="text-muted">No field</span>';
        else {
            badges.forEach(b => {
              const chip = document.createElement('span');
              chip.className = 'badge-formula rounded-pill px-3 mb-3';
              chip.textContent = b.querySelector('.mapping-src-badge-label').textContent;
              chip.dataset.field = b.dataset.field;
              container.appendChild(chip);
            });
        }
        
        modal.dataset.currentRowId = tr.dataset.rowId;
        modal.dataset.currentFormulaInputId = hidden.id;
        const area = UI.get('area_insert');
        if (area) area.value = hidden.value || '';
      }
    };
    
    // Delete Row
    const tdDel = document.createElement('td');
    tdDel.className = 'text-start';
    const delBtn = document.createElement('button');
    delBtn.className = 'btn btn-sm text-danger mt-2';
    delBtn.innerHTML = '<i class="fa-solid fa-trash"></i>';
    delBtn.type = 'button';
    
    if (isRequired) {
        delBtn.disabled = true;
        delBtn.style.opacity = '0.5';
        delBtn.title = 'Required field cannot be removed';
    } else {
        delBtn.onclick = () => { tr.remove(); window.refreshTargetDropdowns(); };
    }
    tdDel.appendChild(delBtn);

    // Ajout au DOM
    tr.append(tdTgt, tdSrc, tdAct, tdDel);
    tbody.appendChild(tr);
    const $tgtSelectize = $(tgtSel).selectize({
        sortField: "text",
        placeholder: "Search Target...",
        dropdownParent: "body",
        onChange: function() { window.refreshTargetDropdowns(); }
    });
    
    if (preselectedTarget && $tgtSelectize[0].selectize) {
        const s = $tgtSelectize[0].selectize;
        s.addOption({value: preselectedTarget, text: preselectedTarget});
        s.addItem(preselectedTarget, true);
    }
    
    $(srcSel).selectize({
        sortField: 'text',
        placeholder: 'Search Source...',
        dropdownParent: 'body',
        onChange: function(value) {
            if(value && this.$input && this.$input[0]) {
                 var event = new Event('change', { bubbles: true });
                 this.$input[0].dispatchEvent(event);
            }
          }
    });
    window.refreshTargetDropdowns();
};

  window.initMappingUI = function() {
    const btn = UI.get('rule-mapping-add');
    const tbody = UI.get('rule-mapping-body');
    if (!btn || !tbody) return;

    if (!tbody.querySelector('tr')) window.addMappingRow(tbody);
    if (btn.dataset.bound) return;
    btn.dataset.bound = '1';
    btn.addEventListener('click', () => window.addMappingRow(tbody));
  };

    // Ensures a mapping row exists for the chosen duplicate field
    window.ensureDuplicateMappingRow = function(targetField) {
        const tbody = UI.get('rule-mapping-body');
        if (!tbody || !targetField) return;

        const wanted = String(targetField).trim();

        const getTgtSel = (tr) =>tr.querySelector('.rule-mapping-target') || tr.querySelector('.js-select-search-locked') || tr.querySelector('td select');

        const getVal = (sel) => {
            if (!sel) return '';
            if (sel.selectize) return (sel.selectize.getValue() || '').trim();
            if ($(sel)[0] && $(sel)[0].selectize) return ($(sel)[0].selectize.getValue() || '').trim();
            return (sel.value || '').trim();
        };

        const norm = (s) => String(s || '').replace(/\*/g, '').trim();
        let row = Array.from(tbody.querySelectorAll('tr')).find(tr => {
            const sel = getTgtSel(tr);
            if (!sel) return false;

            const v = getVal(sel);
            if (v === wanted) return true;

            const opt = (sel.options && sel.selectedIndex >= 0) ? sel.options[sel.selectedIndex] : null;
            const label = norm(opt ? (opt.textContent || opt.text || '') : '');
            return label === norm(wanted);
        });

        if (!row) {
            row = Array.from(tbody.querySelectorAll('tr')).find(tr => {
                const sel = getTgtSel(tr);
                if (!sel) return false;

                const v = getVal(sel);
                const hasSource = tr.querySelectorAll('.mapping-src-badge').length > 0;
                return !v && !hasSource;
            });
        }

        if (!row) {
            window.addMappingRow(tbody, wanted, true);
            row = tbody.lastElementChild;
            return;
        }

        const sel = getTgtSel(row);
        if (!sel) return;

        const selectize = sel.selectize || ($(sel)[0] ? $(sel)[0].selectize : null);
        if (selectize) {
            if (selectize.getValue() !== wanted) {
                selectize.addOption({ value: wanted, text: wanted });
                selectize.setValue(wanted);
            }
            selectize.lock();
        } else {
            sel.value = wanted;
        }

        const delBtn =row.querySelector('.rule-mapping-delete') || row.querySelector('button.text-danger');

        if (delBtn) {
            delBtn.disabled = true;
            delBtn.style.opacity = '0.5';
            delBtn.title = 'Duplicate check field cannot be removed';
            delBtn.onclick = null;
        }
    };
})();

/* ============================================================
 * EDIT MODE (Data hydration)
 * ============================================================ */
(function () {
  const ruleData = window.initialRule || null;
  if (!ruleData) {
    if (typeof window.ruleInitDone === 'function') window.ruleInitDone();
    return;
  }

  window.__EDIT_MODE__ = true;
  const nameInput = UI.get('rulename');

  // Fills the form with JSON sent by the server
  async function hydrateEditFromJson() {
    try {
      if (nameInput) {
        nameInput.value = ruleData.name || '';
        nameInput.classList.add('is-valid');

      }
      const descInput = UI.get('ruledescription');
      if (descInput && ruleData.params?.description) {
        descInput.value = ruleData.params.description;
      }

      if (typeof window.__revealStep2 === 'function') window.__revealStep2();
      else {
          const s2 = UI.get('step-2');
          if(s2) {
              s2.classList.remove('d-none');
              s2.style.opacity = '1';
          }
      }

      // Sequential filling and loading of dependent lists
      const srcSol = UI.get('source-solution');
      if (srcSol && ruleData.connection?.source?.solutionId) {
        UI.setValue(srcSol, ruleData.connection.source.solutionId);
        await window.loadConnectorsFor('source', srcSol.value);
      }
      
      const tgtSol = UI.get('target-solution');
      if (tgtSol && ruleData.connection?.target?.solutionId) {
        UI.setValue(tgtSol, ruleData.connection.target.solutionId);
        await window.loadConnectorsFor('cible', tgtSol.value);
      }

      const srcConn = UI.get('source-connector');
      if (srcConn && ruleData.connection?.source?.connectorId) {
        UI.setValue(srcConn, ruleData.connection.source.connectorId);
        await window.loadModulesFor('source', srcConn.value);
      }

      const tgtConn = UI.get('target-connector');
      if (tgtConn && ruleData.connection?.target?.connectorId) {
        UI.setValue(tgtConn, ruleData.connection.target.connectorId);
        await window.loadModulesFor('cible', tgtConn.value);
      }

      const srcMod = UI.get('source-module');
      if (srcMod && ruleData.connection?.source?.module) {
        UI.setValue(srcMod, ruleData.connection.source.module);
      }

      const tgtMod = UI.get('target-module');
      if (tgtMod && ruleData.connection?.target?.module) {
        UI.setValue(tgtMod, ruleData.connection.target.module);
      }

      // Disable structural fields in edit mode
      [srcSol, tgtSol, srcConn, tgtConn, srcMod, tgtMod].forEach(el => {
        if (!el) return;
        el.disabled = true;
        if (el.selectize) el.selectize.disable();
      });

      // Step 3 Hydration
      const step3 = UI.get('step-3');
      if(step3) {
          UI.toggle(step3, true);
          await window.loadStep3Params();

          if (ruleData.syncOptions?.type) {
             UI.setValue(UI.get('mode'), ruleData.syncOptions.type);
          }
          if (ruleData.syncOptions?.duplicateField) {
            const d = UI.get('duplicate-field');
            if (d) { 
                d.disabled = false; 
                UI.setValue(d, ruleData.syncOptions.duplicateField); 
            }
          }
          if (ruleData.params) {
            Object.entries(ruleData.params).forEach(([k, v]) => {
              UI.setValue(UI.get(k), v);
            });
          }
      }

      // Step 4 Hydration (Filters)
      const step4 = UI.get('step-4');
      if(step4) {
          UI.toggle(step4, true);
          await window.mydLoadRuleFilters();
          
          if (ruleData.filters && ruleData.filters.length > 0) {
              if (typeof window.addFilterRow === 'function') {
                  ruleData.filters.forEach(f => {
                      window.addFilterRow(f.field, f.operator, f.value);
                  });
              } else {
                  console.error('Function addFilterRow not found');
              }
          }
      }

      // Step 5 Hydration (Mapping)
      const step5 = UI.get('step-5');
      if(step5) {
          UI.toggle(step5, true);
          window.initMappingUI();
          
          const tbody = UI.get('rule-mapping-body');
          if (tbody) tbody.innerHTML = ''; 

          (ruleData.mapping || []).forEach(row => {
            window.addMappingRow(tbody);
            const tr = tbody.lastElementChild;
            if(!tr) return;
            
            const tSel = tr.querySelector('.rule-mapping-target');
            if (tSel && row.target) {
                if (tSel.selectize) {
                    tSel.selectize.setValue(row.target);
                } else if ($(tSel)[0] && $(tSel)[0].selectize) {
                    $(tSel)[0].selectize.setValue(row.target);
                } else {
                    tSel.value = row.target;
                }
            }
            const sSel = tr.querySelector('.rule-mapping-source-picker');
            if (sSel && row.source) {
              const srcs = Array.isArray(row.source) ? row.source : row.source.split(';');         
              srcs.filter(Boolean).forEach(s => {
                if (sSel.selectize) {
                    sSel.selectize.setValue(s.trim()); 
                } else if ($(sSel)[0] && $(sSel)[0].selectize) {
                    $(sSel)[0].selectize.setValue(s.trim());
                } else {
                    sSel.value = s.trim();
                    sSel.dispatchEvent(new Event('change'));
                }
              });
            }

            const hidden = tr.querySelector('.rule-mapping-formula-input');
            const slot = tr.querySelector('.formula-slot');
            if (row.formula && hidden) {
              hidden.value = row.formula;
              slot.textContent = row.formula;
              slot.classList.remove('is-empty');
            }
          });
        window.refreshTargetDropdowns();
      }
    if (ruleData.syncOptions?.duplicateField && typeof window.ensureDuplicateMappingRow === 'function') {
          let raw = ruleData.syncOptions.duplicateField;
          let values = [];
          
          if (Array.isArray(raw)) values = raw;
          else if (typeof raw === 'string') values = raw.split(';');
        
          values.forEach(v => {
              if(v && v.trim() !== '') window.ensureDuplicateMappingRow(v.trim());
          });
      }

      const step5El = UI.get('step-5');
      if (step5El && !step5El.classList.contains('d-none')) {
          setTimeout(() => {
              step5El.scrollIntoView({ behavior: 'smooth', block: 'start' });
          }, 300);
      }
    } catch (e) {
      console.error(e);
    } finally {
      if (typeof window.ruleInitDone === 'function') window.ruleInitDone();
    }
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', hydrateEditFromJson, { once: true });
  } else {
    hydrateEditFromJson();
  }
})();

/* ===========================================
 * FUNCTION WIZARD (Formula Editor)
 * =========================================== */
$(function () {
  const insertAtCursor = (el, text) => {
    if (!el) return;
    const [start, end] = [el.selectionStart, el.selectionEnd];
    el.value = el.value.substring(0, start) + text + el.value.substring(end);
    el.selectionStart = el.selectionEnd = start + text.length;
    el.focus();
  };

  const UI_WIZ = {
    sel: $('#function-select'),
    lookupOpts: $('#lookup-options'),
    lookupRule: $('#lookup-rule'),
    lookupField: $('#lookup-field'),
    param: $('#function-parameter'),
    tooltip: $('#function-tooltip'),
    prec: $('#round-precision')
  };

  let tooltipVisible = false;

   // Click on a badge in the list -> Inserts {field}
  $('#toggle-tooltip').on('click', function () {
    tooltipVisible = !tooltipVisible;
    if (tooltipVisible) {
        updateTooltipContent();
        UI_WIZ.tooltip.show();
    } else {
        UI_WIZ.tooltip.hide();
    }
  });
  function updateTooltipContent() {
      const $select = $('#function-select');
      let val = $select[0]?.selectize ? $select[0].selectize.getValue() : $select.val();
      let tip = (val && window.FUNCTION_TOOLTIPS && window.FUNCTION_TOOLTIPS[val]) ? window.FUNCTION_TOOLTIPS[val] : "No description available.";
      UI_WIZ.tooltip.html(tip);
  }

  function populateLookupFieldFromBadges() {
      const $field = UI_WIZ.lookupField;
      const selectize = $field[0].selectize;
      
      $field.empty();
      if (selectize) {
          selectize.clear();
          selectize.clearOptions();
          selectize.disable();
          selectize.settings.placeholder = 'Select a rule first...';
          selectize.updatePlaceholder();
      }

      const $badges = $('#formula-selected-fields .badge-formula');
      
      if ($badges.length > 0) {
          $badges.each(function() {
              const val = $(this).data('field');
              const txt = $(this).text().trim();
              
              $field.append(new Option(txt, val));
              if (selectize) selectize.addOption({value: val, text: txt});
          });
          
          if (selectize) selectize.refreshOptions(false);
      } else {
           const msg = 'No source field selected';
           $field.append(new Option(msg, ''));
           if (selectize) {
               selectize.settings.placeholder = msg;
               selectize.updatePlaceholder();
           }
      }
  }

  UI_WIZ.sel.off('change').on('change', function () {
    const val = $(this).val();
    const isMdw = val.startsWith('mdw_');
    const isRound = val === 'round';
    const isLookup = val === 'lookup';

    $('#function-parameter-input').toggle(!isLookup);
    UI_WIZ.param.toggle(!isMdw);
    $('#round-precision-input').toggle(isRound);
    UI_WIZ.lookupOpts.toggle(isLookup); 
    
    if (tooltipVisible) updateTooltipContent();  
    if (isLookup) {
        populateLookupFieldFromBadges();

        const srcSelect = document.getElementById('source-connector');
        const tgtSelect = document.getElementById('target-connector');
        const srcId = srcSelect ? srcSelect.value : 0;
        const tgtId = tgtSelect ? tgtSelect.value : 0;
        const endpoint = window.LOOKUP_ENDPOINT || (typeof lookupgetrule !== 'undefined' ? lookupgetrule : null);
        const selectizeRule = UI_WIZ.lookupRule[0].selectize;
        if(selectizeRule) { 
            selectizeRule.clear();
            selectizeRule.clearOptions();
            selectizeRule.settings.placeholder = 'Loading rules...';
            selectizeRule.updatePlaceholder();
            selectizeRule.disable();
        }

        if (endpoint && srcId && tgtId) {
            $.get(endpoint, { arg1: srcId, arg2: tgtId }, (res) => {
                UI_WIZ.lookupRule.empty().append(new Option('Select a rule...', ''));
                if(selectizeRule) { 
                    selectizeRule.settings.placeholder = 'Select a rule...';
                    selectizeRule.updatePlaceholder();
                }

                if (Array.isArray(res) && res.length > 0) {
                    res.forEach(r => {
                        UI_WIZ.lookupRule.append(new Option(r.name, r.id));
                        if(selectizeRule) selectizeRule.addOption({value: r.id, text: r.name});
                    });
                    if(selectizeRule) selectizeRule.enable();
                } else {
                    UI_WIZ.lookupRule.append(new Option('No matching rules found', ''));
                }
            });
        } else {
             UI_WIZ.lookupRule.empty().append(new Option('Please select connectors first', ''));
        }
    }
  });

  // --- LOOKUP ---
  UI_WIZ.lookupRule.off('change').on('change', function() {
      const ruleId = this.selectize ? this.selectize.getValue() : $(this).val();
      const selectizeField = UI_WIZ.lookupField[0].selectize;

      if (!selectizeField) return;

      if (ruleId) {
          selectizeField.enable();
          selectizeField.settings.placeholder = 'Select...';
          selectizeField.updatePlaceholder();
      } else {
          selectizeField.disable();
          selectizeField.settings.placeholder = 'Select...';
          selectizeField.updatePlaceholder();
      }
  });

  $('#mapping-formula').on('shown.bs.modal', function () {
      if (UI_WIZ.sel.val() === 'lookup') {
          populateLookupFieldFromBadges();
          UI_WIZ.lookupRule.trigger('change');
      }
  });
  
  $('#submit-lookup').on('click', function() {
    let f = UI_WIZ.lookupField.val();
    if(UI_WIZ.lookupField[0].selectize) f = UI_WIZ.lookupField[0].selectize.getValue();
    
    let r = UI_WIZ.lookupRule.val();
    if(UI_WIZ.lookupRule[0].selectize) r = UI_WIZ.lookupRule[0].selectize.getValue();

    if (!f || !r) return;
    
    const e1 = $('#lookup-error-empty').is(':checked') ? 1 : 0;
    const e2 = $('#lookup-error-not-found').is(':checked') ? 1 : 0;
    
    insertAtCursor(document.getElementById('area_insert'), `lookup({${f}}, "${r}", ${e1}, ${e2})`);
  });
  $('#formula-selected-fields').on('click', '.badge-formula', function () {
    const field = $(this).data('field') || $(this).text().trim();
    if (field) insertAtCursor(document.getElementById('area_insert'), `{${field}}`);
  });
  $('#insert-function-parameter').on('click', function () {
    const func = UI_WIZ.sel.val();
    if (!func) return;
    const val = UI_WIZ.param.val().trim();
    let call = `${func}()`;

    if (func === 'round') {
      const p = parseInt(UI_WIZ.prec.val());
      call = `round(${val}, ${isNaN(p) ? 0 : p})`;
    } else if (func.startsWith('mdw_')) {
      call = `"${func}"`;
    } else if (val) {
      const selectedOption = UI_WIZ.sel.find('option:selected');
      const cat = selectedOption.data('type'); 
      call = (cat === 1 || cat === 4) ? `${func}(${val})` : `${func}("${val}")`;
    }

    insertAtCursor(document.getElementById('area_insert'), call);
    UI_WIZ.param.val('');
  });

  // Saves the formula into the hidden field of the mapping row
  $('#mapping-formula-save').on('click', function () {
    const modal = document.getElementById('mapping-formula');
    const id = modal.dataset.currentRowId;
    const inputId = modal.dataset.currentFormulaInputId;
    if (!id || !inputId) return;
    const tr = document.querySelector(`tr[data-row-id="${id}"]`);
    if (tr) {
      const val = $('#area_insert').val().trim();
      document.getElementById(inputId).value = val;
      const slot = tr.querySelector('.formula-slot');
      slot.textContent = val;
      slot.classList.toggle('is-empty', !val);
    }
  });
    $('#mapping-formula').on('hidden.bs.modal', function () {
      UI_WIZ.tooltip.hide();
      tooltipVisible = false;
      if(UI_WIZ.sel[0].selectize) UI_WIZ.sel[0].selectize.clear();
  });
});

/* ===========================================
 * SAVE (Final rule save)
 * =========================================== */
$(document).ready(function() {
    $(document).off('click', '#rule-save').on('click', '#rule-save', async function(e) {
        e.preventDefault();
        
        const saveBtn = document.getElementById('rule-save');
        const url = saveBtn.getAttribute('data-path-save');
        if (!url) return alert('Missing save endpoint');

        const fd = new FormData();
        const add = (k, v) => fd.append(k, v);
        const getVal = (id) => document.getElementById(id)?.value || '';
        const getTxt = (id) => { 
            const el = document.getElementById(id); 
            return el && el.options && el.selectedIndex >= 0 ? el.options[el.selectedIndex].text : ''; 
        };

        add('name', getVal('rulename'));
        add('description', getVal('ruledescription'));
        add('src_solution_id', getVal('source-solution'));
        add('tgt_solution_id', getVal('target-solution'));
        add('src_solution_name', getTxt('source-solution').toLowerCase());
        add('tgt_solution_name', getTxt('target-solution').toLowerCase());
        add('src_connector_id', getVal('source-connector'));
        add('tgt_connector_id', getVal('target-connector'));
        add('src_module', getVal('source-module'));
        add('tgt_module', getVal('target-module'));

        // --- STEP 3 : PARAMÈTRES ---
        const pContainer = document.getElementById('step-3-params-container');
        if (pContainer) {
            const elements = Array.from(pContainer.querySelectorAll('input, select, textarea'));      
            elements.forEach(el => {
                let name = el.name;
                let value = el.value;
                const isDuplicate = (el.id === 'duplicate-field');

                // DUPLICATE FIELD
                if (isDuplicate) {
                    const container = document.getElementById('duplicate-badges-container');
                    let badgeVals = [];
                    if (container) {
                        badgeVals = Array.from(container.querySelectorAll('.mapping-src-badge'))
                                         .map(b => b.dataset.field);
                    }
                    value = badgeVals.join(';');
                    add('duplicate_fields', value); 
                    return;
                }

                if (!name || el.disabled) return;
                if ((el.type === 'checkbox' || el.type === 'radio') && !el.checked) return;
                if (name === 'mode') name = 'sync_mode';

                add(name, value);
            });
        } else {
            const mode = document.getElementById('mode') || document.getElementById('sync-mode');
            if(mode) add('sync_mode', mode.value);
        }

        // --- STEP 4 & 5 ---
        const mapping = { fields: {}, formulas: {} };
        document.querySelectorAll('#rule-mapping-body tr').forEach(tr => {
             const tgt = tr.querySelector('.rule-mapping-target')?.value;
             if(!tgt) return;
             const badgeEls = tr.querySelectorAll('.mapping-src-badge');
             let srcs = [];
             badgeEls.forEach(b => srcs.push(b.dataset.field));
             const form = tr.querySelector('.rule-mapping-formula-input')?.value;
             const hasFormula = form && form.trim() !== '';
             const hasSource = srcs.length > 0;
             if (!hasSource && !hasFormula) {
                 return; 
             }
             if (!mapping.fields[tgt]) mapping.fields[tgt] = [];
             if (!mapping.formulas[tgt]) mapping.formulas[tgt] = [];
             if(hasSource) {
                 mapping.fields[tgt].push(...srcs); 
             } else {
                 mapping.fields[tgt].push('');
             }
             if(hasFormula) {
                 mapping.formulas[tgt].push(form);
             }
        });
        
        const filters = [];
        document.querySelectorAll('#rule-filters-list li').forEach(li => {
             filters.push({ field: li.dataset.field, operator: li.dataset.operator, value: li.dataset.value || '' });
        });

        add('filters', JSON.stringify(filters));
        Object.entries(mapping.fields).forEach(([t, arr]) => arr.forEach(v => add(`champs[${t}][]`, v)));
        Object.entries(mapping.formulas).forEach(([t, arr]) => arr.forEach(v => add(`formules[${t}][]`, v)));

        if (window.initialRule?.mode === 'edit') add('rule_id', window.initialRule.id);
        const oldHtml = saveBtn.innerHTML;
        saveBtn.disabled = true;
        saveBtn.innerHTML = '<span class="spinner-border spinner-border-sm"></span> Saving...';

        try {
            const res = await fetch(url, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd });
            const text = await res.text();
            if (!res.ok) throw new Error(text);
            
            let json;
            try { json = JSON.parse(text); } catch(e) { throw new Error(text); }

            if (json.error) throw new Error(json.error);
            if (json.redirect) window.location.assign(json.redirect);
            else alert('Rule saved.');
        } catch (e) {
            console.error("Erreur Save:", e);
            let msg = e.message;
            if (msg.includes('Array to string')) msg = 'Erreur: Format de données incorrect.';
            alert('Save failed: ' + msg);
        } finally {
            saveBtn.disabled = false;
            saveBtn.innerHTML = oldHtml;
        }
    });
});

/* ===========================================
 * SIMULATION (Debug Version)
 * =========================================== */
(function () {
  const modal = UI.get('mapping-simulation');
  if (!modal) return;

  const endpoints = { run: modal.getAttribute('data-endpoint-run'), count: modal.getAttribute('data-endpoint-count') };
  const EL = { res: modal.querySelector('#sim-result'), alert: modal.querySelector('#sim-alert'), input: modal.querySelector('#sim-record-id') };

  const showAlert = (msg, type = 'danger') => {
    EL.alert.className = `alert alert-${type}`;
    EL.alert.textContent = msg;
    EL.alert.classList.remove('d-none');
  };

  async function runSim(manual) {
    EL.alert.classList.add('d-none');
    if (!endpoints.run) return showAlert('Missing endpoint');

    const fd = new FormData();
    const getVal = (id) => UI.get(id)?.value || '';
    const getTxt = (id) => { const el = UI.get(id); return el?.options[el.selectedIndex]?.text?.trim().toLowerCase() || ''; };

    fd.append('src_solution_id', getVal('source-solution'));
    fd.append('tgt_solution_id', getVal('target-solution'));
    fd.append('src_solution_name', getTxt('source-solution'));
    fd.append('tgt_solution_name', getTxt('target-solution'));
    fd.append('src_connector_id', getVal('source-connector'));
    fd.append('tgt_connector_id', getVal('target-connector'));
    fd.append('src_module', getVal('source-module'));
    fd.append('tgt_module', getVal('target-module'));

    const pContainer = UI.get('step-3-params-container');
    if (pContainer) {
      pContainer.querySelectorAll('input, select').forEach(el => {
        if(el.name) {
            fd.append(el.name === 'mode' ? 'sync_mode' : el.name, el.value);
        }
      });
    }

    const rows = Array.from(document.querySelectorAll('#rule-mapping-body tr'));
    
    rows.forEach(tr => {
        const tgt = tr.querySelector('.rule-mapping-target')?.value;
        if(!tgt) return;
        
        const badges = Array.from(tr.querySelectorAll('.mapping-src-badge'));
        badges.forEach(b => fd.append(`champs[${tgt}][]`, b.dataset.field));  
        const form = tr.querySelector('.rule-mapping-formula-input')?.value;
        if(form) fd.append(`formules[${tgt}][]`, form);
    });

    if (manual) {
      const id = EL.input.value.trim();
      if (!id) return showAlert('ID required', 'warning');
      fd.append('query', id);
    }
    
    try {
      
      const res = await fetch(endpoints.run, { method: 'POST', headers: { 'X-Requested-With': 'XMLHttpRequest' }, body: fd });      
      const text = await res.text();
      EL.res.innerHTML = text;
      
    } catch (e) {
      EL.res.innerHTML = '';
      showAlert('Simulation network error');
    } finally {
        console.groupEnd();
    }
  }

  modal.querySelector('#sim-run-manual')?.addEventListener('click', () => runSim(true));
  modal.querySelector('#sim-run-simple')?.addEventListener('click', () => runSim(false));
})();
/* ===========================================
 * SELECTIZE INIT (Global initialization)
 * =========================================== */
$(document).ready(function() {
    var renderSolution = function(item, escape) {
        if (!item.value || item.value === '') return '<div style="display:none !important;"></div>';

        var label = item.text || '';
        if (label) label = label.charAt(0).toUpperCase() + label.slice(1);
        var slug = (item.slug || item.text || '').toLowerCase().trim();
        var currentUrl = window.location.pathname;
        var rootPath = currentUrl.split('/rule')[0];
        var imgPath = rootPath + '/rule/assets/solution-icon/' + escape(slug);

        return '<div class="d-flex align-items-center" style="display: flex; align-items: center; padding: 5px;">' +
               '<img src="' + imgPath + '" style="width: 24px; height: 24px; object-fit: contain; margin-right: 10px;" onerror="this.style.display=\'none\'" />' + 
               '<span>' + escape(label) + '</span>' +
               '</div>';
    };

    $('select').each(function() {
        var $el = $(this);
        var id = $el.attr('id');
        if ($el[0].selectize) return;
        var options = {
            sortField: 'text',
            placeholder: 'Search...',
            onChange: function(value) {
                if(this.$input && this.$input[0]) {
                     var event = new Event('change', { bubbles: true });
                     this.$input[0].dispatchEvent(event);
                }
            }
        };
        if (id === 'source-solution' || id === 'target-solution') {
            options.valueField = 'value';
            options.labelField = 'text';
            options.searchField = ['text'];
            options.render = { option: renderSolution, item: renderSolution };
            
            var selectizeOpts = [];
            $el.find('option').each(function() {
                if($(this).val()) {
                    selectizeOpts.push({
                        value: $(this).val(),
                        text: $(this).text(),
                        slug: $(this).text().toLowerCase().trim()
                    });
                }
            });
        }

        $el.selectize(options);
    });
});

/* ===========================================
 * TEMPLATE LOGIC (Template management Step 1)
 * =========================================== */
(function () {
  const $switchEl = $('#template-mode-switch');
  const zone = document.getElementById('rule-template-zone');
  const step2 = document.getElementById('step-2');
  if (!step2 || !zone) return;

  const path = step2.getAttribute('data-path-templates');
  const solutionsMap = {};
  $('#source-solution option, #target-solution option').each(function() {
      const id = $(this).val();
      const slug = $(this).attr('data-solution-slug');
      if (id && slug) {
          solutionsMap[id] = slug;
      }
  });

  const loadTemplates = () => {
    if ($switchEl.length && !$switchEl.is(':checked')) return;

    const sId = $('#source-solution').val();
    const tId = $('#target-solution').val();
    const sSlug = solutionsMap[sId];
    const tSlug = solutionsMap[tId];

    if (sSlug && tSlug) {
      zone.innerHTML = '<div class="text-center py-4"><div class="spinner-border text-primary"></div></div>';
      
      if (typeof UI !== 'undefined' && UI.fetchHtml) {
        UI.fetchHtml(path, { src_solution: sSlug, tgt_solution: tSlug })
          .then(html => zone.innerHTML = html || '<p class="text-center text-muted">No templates found.</p>')
          .catch(() => zone.innerHTML = '<p class="text-danger text-center">Error loading templates.</p>');
      } else {
        $.get(path, { src_solution: sSlug, tgt_solution: tSlug })
         .done(html => zone.innerHTML = html)
         .fail(() => zone.innerHTML = '<p class="text-danger">Error.</p>');
      }
    }
  };
  
$switchEl.on('change', function() {
    const isTpl = $(this).is(':checked');
    $('#source-module-group, #target-module-group').toggleClass('d-none', isTpl);
    $('#step-templates').toggleClass('d-none', !isTpl);
    
    if(isTpl) {
        $('#step-3, #step-4, #step-5').addClass('d-none');
        loadTemplates();
    } else {
        if (typeof window.tryRevealStep3 === 'function') {
            window.tryRevealStep3(); 
        }
    }
  });

  $('#source-solution, #target-solution').on('change', function() {
      setTimeout(loadTemplates, 50); 
  });

  $(document).on('click', '.js-template-choose', function(e) {
    const btn = e.target.closest('.js-template-choose');
    if (!btn) return;
    
    $('.template-card').removeClass('is-selected border-primary shadow');
    $(btn).closest('.template-card').addClass('is-selected border-primary shadow');
    
    let inp = document.getElementById('selected-template-name');
    if (!inp) {
        inp = document.createElement('input'); 
        inp.type='hidden'; 
        inp.id='selected-template-name'; 
        document.getElementById('step-1').appendChild(inp);
    }
    inp.value = btn.getAttribute('data-template-name');
  });

  $('#rule-save-template').on('click', async function(e) {
      e.preventDefault();
      const $btn = $(this);
      const url = $btn.attr('data-path-template-apply');
      const tplName = $('#selected-template-name').val();
      const ruleName = $('#rulename').val();
      
      if (!ruleName || !tplName) {
          alert('Please enter a rule name and select a template.');
          return;
      }

      $btn.prop('disabled', true).html('<span class="spinner-border spinner-border-sm"></span> Saving...');

      try {
        const response = await fetch(url, {
             method: 'POST',
             headers: {'Content-Type': 'application/json'},
             body: JSON.stringify({
               ruleName: ruleName,
               templateName: tplName,
               connectorSourceId: $('#source-connector').val(),
               connectorTargetId: $('#target-connector').val()
             })
        });
        const json = await response.json();
        
        if (json.redirect) window.location.assign(json.redirect);
        else {
            alert('Error: ' + (json.message || 'Unknown error'));
            $btn.prop('disabled', false).text('Save');
        }
      } catch (err) {
        console.error(err);
        alert('Network Error');
        $btn.prop('disabled', false).text('Save');
      }
  });

})();

/* ===========================================
 * PICKLIST
 * =========================================== */
(function () {
  const cache = new Map();

  function endpoint() {
    return document.getElementById('mapping-formula')?.dataset.moduleFieldsUrl || '';
  }

  function ctx(side) {
    const conn = document.getElementById(side === 'src' ? 'source-connector' : 'target-connector')?.value || '';
    const mod  = document.getElementById(side === 'src' ? 'source-module' : 'target-module')?.value || '';
    const type = (side === 'src') ? 'source' : 'cible';
    return { conn, mod, type };
  }

  function el(side) {
    return {
      group: document.getElementById(side === 'src' ? 'pl_src_group' : 'pl_tgt_group'),
      val:   document.getElementById(side === 'src' ? 'pl_src_val'   : 'pl_tgt_val'),
    };
  }

  function setDisabled(selectEl, disabled) {
    if (!selectEl) return;
    selectEl.disabled = disabled;
    if (selectEl.selectize) {
      disabled ? selectEl.selectize.disable() : selectEl.selectize.enable();
    }
  }

  /**
   * @param {HTMLSelectElement} selectEl
   * @param {string} placeholder
   * @param {Object} optionsMap
   */
  function fillSelect(selectEl, placeholder, optionsMap) {
    if (!selectEl) return;

    // Native select
    selectEl.innerHTML = `<option value="">${placeholder}</option>`;
    Object.entries(optionsMap || {}).forEach(([v, text]) => {
      selectEl.appendChild(new Option(String(text), String(v)));
    });

    // Selectize sync
    if (selectEl.selectize) {
      const s = selectEl.selectize;
      s.clear(true);
      s.clearOptions();

      s.settings.placeholder = placeholder;
      s.updatePlaceholder();

      Object.entries(optionsMap || {}).forEach(([v, text]) => {
        s.addOption({ value: String(v), text: String(text) });
      });

      s.refreshOptions(false);
    }
  }

  async function fetchPicklists(conn, mod, type) {
    const url = endpoint();
    if (!url) throw new Error('Missing #mapping-formula[data-module-fields-url]');

    const qs = new URLSearchParams({
      connector_id: conn,
      module: mod,
      type: type,
      with_picklists: '1',
    });

    const res = await fetch(`${url}?${qs.toString()}`, {
      headers: {
        'X-Requested-With': 'XMLHttpRequest',
        'Accept': 'application/json'
      }
    });

    const text = await res.text();
    let json;
    try { json = JSON.parse(text); } catch { throw new Error('Invalid JSON response'); }
    if (!res.ok) throw new Error(json?.error || 'Request failed');

    return json;
  }

  async function loadSide(side) {
    const { group, val } = el(side);
    const { conn, mod, type } = ctx(side);

    fillSelect(group, 'Select Field', {});
    setDisabled(group, true);

    fillSelect(val, 'Select a field first...', {});
    setDisabled(val, true);

    if (!conn || !mod) return;

    const key = `${conn}|${mod}|${type}`;
    if (!cache.has(key)) {
      const data = await fetchPicklists(conn, mod, type);
      cache.set(key, {
        picklists: data.picklists || {},
        meta: data.meta || {}
      });
    }

    const picklists = cache.get(key)?.picklists || {};
    const fields = Object.keys(picklists);

    // Field UI: value-only (fieldName)
    const fieldOptions = {};
    fields.forEach((f) => { fieldOptions[f] = f; });

    fillSelect(group, fields.length ? 'Select Field' : 'No picklists', fieldOptions);
    setDisabled(group, fields.length === 0);
  }

  function getSelectValue(selectEl) {
    if (!selectEl) return '';
    return selectEl.selectize ? (selectEl.selectize.getValue() || '') : (selectEl.value || '');
  }

  function escapeForDoubleQuotes(str) {
    return String(str).replace(/\\/g, '\\\\').replace(/"/g, '\\"');
  }

  function insertAtCursor(textarea, textToInsert) {
    if (!textarea) return;

    textarea.focus();

    const start = textarea.selectionStart ?? textarea.value.length;
    const end   = textarea.selectionEnd ?? textarea.value.length;

    textarea.value =
      textarea.value.slice(0, start) +
      textToInsert +
      textarea.value.slice(end);

    const newPos = start + textToInsert.length;
    textarea.setSelectionRange(newPos, newPos);
  }

  window.loadPlValues = function (side, fieldName) {
    const { val } = el(side);
    const { conn, mod, type } = ctx(side);
    const key = `${conn}|${mod}|${type}`;

    const picklists = cache.get(key)?.picklists || {};
    const opts = picklists[fieldName];

    if (!opts) {
      fillSelect(val, 'No values', {});
      setDisabled(val, true);
      return;
    }

    const valueOnly = {};
    Object.keys(opts).forEach((v) => { valueOnly[v] = v; });

    fillSelect(val, 'Select Value', valueOnly);
    setDisabled(val, false);
  };

  // onclick="insertPl('src')"
  window.insertPl = function (side) {
    const textarea = document.getElementById('area_insert');
    const { val } = el(side);

    const pickedValue = getSelectValue(val);
    if (!pickedValue) return;

    const token = `"${escapeForDoubleQuotes(pickedValue)}"`;
    insertAtCursor(textarea, token);
  };

  function bindChange(selectId, side) {
    const sel = document.getElementById(selectId);
    if (!sel || sel.dataset.plBound) return;
    sel.dataset.plBound = '1';

    sel.addEventListener('change', () => {
      const v = getSelectValue(sel);
      window.loadPlValues(side, v);
    });
  }

  document.addEventListener('DOMContentLoaded', () => {
    const modal = document.getElementById('mapping-formula');
    if (!modal) return;

    bindChange('pl_src_group', 'src');
    bindChange('pl_tgt_group', 'tgt');

    modal.addEventListener('shown.bs.modal', async () => {
      await loadSide('src');
      await loadSide('tgt');
    });
  });
})();